import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  HStack,
  Text,
  Input,
  IconButton,
  Avatar,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import {
  FiArrowLeft,
  FiZoomIn,
  FiZoomOut,
  FiShare2,
  FiDownload,
  FiTag,
  FiLayers,
  FiLink2,
  FiCheck,
} from 'react-icons/fi'
import { FileItem } from '@/types'
import { useAuth } from '@/contexts/AuthContext'
import apiService from '@/services/api'
import FilePermissionsModal from '@/components/Modals/FilePermissionsModal'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useAppDispatch } from '@/store'
import { renameFile } from '@/store/slices/filesSlice'

// Mirrors the server-side validation in FileController@rename
const INVALID_NAME_CHARS = /[/\\:*?"<>|]/

interface PreviewTopBarProps {
  file: FileItem
  scale: number
  onScaleChange: (scale: number) => void
  onBack: () => void
  onTagsClick: () => void
  onRelatedDocumentsClick?: () => void
  relatedDocumentsDisabled?: boolean
  onLinkDocumentClick?: () => void
  onFileRenamed?: (file: FileItem) => void
}

export default function PreviewTopBar({
  file,
  scale,
  onScaleChange,
  onBack,
  onTagsClick,
  onRelatedDocumentsClick,
  relatedDocumentsDisabled,
  onLinkDocumentClick,
  onFileRenamed,
}: PreviewTopBarProps) {
  const { t } = useAppTranslation('files')
  const { user } = useAuth()
  const toast = useToast()
  const dispatch = useAppDispatch()
  const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useDisclosure()
  const [isRenaming, setIsRenaming] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const zoomIn = () => onScaleChange(Math.min(3.0, scale + 0.25))
  const zoomOut = () => onScaleChange(Math.max(0.25, scale - 0.25))

  const handleShare = () => {
    onShareOpen()
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      toast({ title: 'Link copied to clipboard', status: 'success', duration: 2000, isClosable: true })
    } catch {
      toast({ title: 'Failed to copy link', status: 'error', duration: 2000, isClosable: true })
    }
  }

  const handleDownload = async () => {
    try {
      const downloadUrl = await apiService.generateSecureFileUrl(file.id, 'download')
      window.open(downloadUrl, '_blank')
    } catch {
      const fallbackUrl = apiService.getFileDownloadUrl(file.id)
      window.open(fallbackUrl, '_blank')
    }
  }

  const fileName = file.display_name || file.original_name || file.name

  const startRename = useCallback(() => {
    setDraftName(fileName)
    setIsRenaming(true)
  }, [fileName])

  const cancelRename = useCallback(() => {
    setIsRenaming(false)
    setDraftName('')
  }, [])

  useEffect(() => {
    if (isRenaming) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isRenaming])

  const submitRename = useCallback(async () => {
    const trimmed = draftName.trim()

    if (!trimmed || trimmed === fileName) {
      cancelRename()
      return
    }

    if (
      trimmed.length > 255 ||
      INVALID_NAME_CHARS.test(trimmed) ||
      trimmed.startsWith('.') ||
      trimmed.includes('..')
    ) {
      toast({
        title: t('filePreview.rename.invalid'),
        description: t('filePreview.rename.invalidDescription'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    try {
      setIsSaving(true)
      const updated = await dispatch(renameFile({ fileId: file.id, displayName: trimmed })).unwrap()
      toast({
        title: t('filePreview.rename.success'),
        description: t('filePreview.rename.successDescription', { name: trimmed }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      onFileRenamed?.(updated)
      setIsRenaming(false)
      setDraftName('')
    } catch (error: any) {
      console.error('Failed to rename file:', error)
      toast({
        title: t('filePreview.rename.failed'),
        description:
          error?.response?.data?.error ||
          error?.message ||
          t('filePreview.rename.failedDescription'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setIsSaving(false)
    }
  }, [draftName, fileName, cancelRename, toast, t, dispatch, file.id, onFileRenamed])

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        submitRename()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        cancelRename()
      }
    },
    [submitRename, cancelRename]
  )

  return (
    <Box
      bg="white"
      borderBottom="1px solid"
      borderColor="gray.200"
      px={4}
      py={2}
      position="sticky"
      top={0}
      zIndex={100}
      flexShrink={0}
    >
      <HStack justify="space-between" align="center">
        {/* Left: Back + File info */}
        <HStack spacing={3} flex={1} minW={0}>
          <IconButton
            aria-label="Go back"
            icon={<FiArrowLeft />}
            variant="ghost"
            size="sm"
            onClick={onBack}
            color="gray.600"
            _hover={{ bg: 'gray.100' }}
            flexShrink={0}
          />
          {isRenaming ? (
            <HStack spacing={1} flex={1} minW={0}>
              <Input
                ref={inputRef}
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={handleRenameKeyDown}
                onBlur={() => {
                  if (!isSaving) cancelRename()
                }}
                size="sm"
                fontSize="md"
                fontWeight="600"
                variant="outline"
                flex={1}
                minW={0}
                maxW="480px"
                isDisabled={isSaving}
                aria-label={t('filePreview.rename.label')}
              />
              <Tooltip label={t('filePreview.rename.save')}>
                <IconButton
                  aria-label={t('filePreview.rename.save')}
                  icon={<FiCheck />}
                  size="sm"
                  colorScheme="brand"
                  isLoading={isSaving}
                  // Commit before the input's blur handler cancels the edit
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={submitRename}
                  flexShrink={0}
                />
              </Tooltip>
            </HStack>
          ) : (
            <Tooltip label={t('filePreview.rename.tooltip')} openDelay={600}>
              <Text
                fontWeight="600"
                fontSize="md"
                color="gray.900"
                noOfLines={1}
                lineHeight="1.3"
                minW={0}
                cursor="text"
                userSelect="none"
                onDoubleClick={startRename}
              >
                {fileName}
              </Text>
            </Tooltip>
          )}
          <Tooltip label="Tags">
            <IconButton
              aria-label="Manage tags"
              icon={<FiTag size="14px" />}
              variant="ghost"
              size="xs"
              color="gray.500"
              _hover={{ bg: 'gray.100', color: 'brand.500' }}
              onClick={onTagsClick}
              flexShrink={0}
            />
          </Tooltip>
        </HStack>

        {/* Right: Related + Zoom + Actions */}
        <HStack spacing={1} flexShrink={0}>
          {onRelatedDocumentsClick && (
            <Tooltip
              label={
                relatedDocumentsDisabled
                  ? t('filePreview.related.tooltipDisabled')
                  : t('filePreview.related.tooltip')
              }
            >
              <IconButton
                aria-label={t('filePreview.related.tooltip')}
                icon={<FiLayers size="16px" />}
                variant="ghost"
                size="sm"
                color="gray.600"
                _hover={{ bg: 'gray.100' }}
                onClick={onRelatedDocumentsClick}
                isDisabled={relatedDocumentsDisabled}
              />
            </Tooltip>
          )}

          {onLinkDocumentClick && (
            <Tooltip label={t('filePreview.linkDocuments.tooltip')}>
              <IconButton
                aria-label={t('filePreview.linkDocuments.tooltip')}
                icon={<FiLink2 size="16px" />}
                variant="ghost"
                size="sm"
                color="gray.600"
                _hover={{ bg: 'gray.100' }}
                onClick={onLinkDocumentClick}
              />
            </Tooltip>
          )}

          {/* Zoom controls */}
          <HStack
            spacing={1}
            bg="gray.50"
            borderRadius="lg"
            px={2}
            py={1}
            border="1px solid"
            borderColor="gray.200"
          >
            <IconButton
              aria-label="Zoom out"
              icon={<FiZoomOut size="14px" />}
              size="xs"
              variant="ghost"
              onClick={zoomOut}
              isDisabled={scale <= 0.25}
            />
            <Text fontSize="xs" fontWeight="500" color="gray.600" minW="40px" textAlign="center">
              {Math.round(scale * 100)}%
            </Text>
            <IconButton
              aria-label="Zoom in"
              icon={<FiZoomIn size="14px" />}
              size="xs"
              variant="ghost"
              onClick={zoomIn}
              isDisabled={scale >= 3.0}
            />
          </HStack>

          <Tooltip label="Share">
            <IconButton
              aria-label="Share"
              icon={<FiShare2 size="16px" />}
              variant="ghost"
              size="sm"
              color="gray.600"
              _hover={{ bg: 'gray.100' }}
              onClick={handleShare}
            />
          </Tooltip>

          <Tooltip label="Download">
            <IconButton
              aria-label="Download"
              icon={<FiDownload size="16px" />}
              variant="ghost"
              size="sm"
              color="gray.600"
              _hover={{ bg: 'gray.100' }}
              onClick={handleDownload}
            />
          </Tooltip>

          <Avatar size="sm" name={user?.name} ml={1} />
        </HStack>
      </HStack>

      <FilePermissionsModal
        isOpen={isShareOpen}
        onClose={onShareClose}
        fileId={file.id}
        fileName={fileName}
      />
    </Box>
  )
}
